import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import * as graphql from '@treams/treams-network';
import { GeometryObserver } from '@treams/treams-ui/common/Popover';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { tokens } from '@treams/treams-ui/theme/tokens';

import Tooltip from '../../../kakapo-ui-kit/components/tooltips/Tooltip';

const Bullet = styled(Flex)<{ filled?: boolean; crowded?: boolean; enabled?: boolean }>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	background: #dae0e8;
	overflow: hidden;

	${(props) => !props.filled && `transform: scale(0.25, 0.25);`}
	${(props) => props.crowded && `transform: rotateZ(45deg);`}
	${(props) => props.enabled === false && `opacity: 0.25;`}
`;

const Fill = styled.div<{ party: graphql.EvaluationSubmissionParty }>`
	width: 32px;
	height: 32px;
	background: ${(props) =>
		props.party === graphql.EvaluationSubmissionParty.Employee
			? props.theme.colors.employee
			: props.theme.colors.manager};
`;

const Dot = styled.div`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	background: white;
	border-radius: 8px;
`;

const Label = styled.div`
	color: ${tokens.text.secondary};
	text-overflow: ellipsis;
	overflow: hidden;
	flex-shrink: 0;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	padding-top: 10px;
	margin: 0px -20px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function CompetencyDisplayLevel(props: {
	employee: boolean;
	manager: boolean;
	level: graphql.CompetencyLevelOption;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [hover, setHover] = useState(false);

	return (
		<Box
			style={{ position: 'relative' }}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{hover && props.level.enabled && (
				<Tooltip wide center grow>
					<QuillText
						value={localize(props.level.criteria ?? [])?.text}
						placeholder={t('evaluation.submission.competency.levels.noDescription')}
					/>
					<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
				</Tooltip>
			)}
			<Bullet
				filled={props.employee || props.manager}
				crowded={props.employee && props.manager}
				enabled={props.level.enabled}
			>
				{props.employee && <Fill party={graphql.EvaluationSubmissionParty.Employee} />}
				{props.manager && <Fill party={graphql.EvaluationSubmissionParty.Manager} />}
				{(props.employee || props.manager) && <Dot />}
			</Bullet>
		</Box>
	);
}

export default function CompetencyDisplay(props: {
	levels: graphql.CompetencyLevelOption[];
	employee?: number;
	manager?: number;
	labels?: boolean;
}) {
	const { localize } = useLocalize();
	const ref = useRef(null);

	// Use enabled levels for the left and right legend text.
	const enabledLevels = props.levels.filter((level) => !!level.enabled);

	const leftLabel = enabledLevels.length > 0 && props.labels !== false && (
		<Box mr={2}>
			<Label>{localize(enabledLevels[0].level.title).text}</Label>
		</Box>
	);

	const rightLabel = enabledLevels.length > 0 && props.labels !== false && (
		<Box ml={2}>
			<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
		</Box>
	);

	const bullets = props.levels.map((level, i) => {
		// Filter bullets in this map, not before. This to preserve
		// the correct active index.
		if (!level.enabled) {
			return null;
		}

		return (
			<CompetencyDisplayLevel
				key={i}
				level={level}
				employee={i === props.employee}
				manager={i === props.manager}
			/>
		);
	});

	return (
		<Box ref={ref} width="100%">
			<GeometryObserver innerRef={ref}>
				{(bounds) =>
					bounds.width < 320 ? (
						<Box>
							<Flex alignItems="center" justifyContent="space-between">
								{bullets}
							</Flex>
							<Flex justifyContent={'space-between'}>
								{leftLabel}
								{rightLabel}
							</Flex>
						</Box>
					) : (
						<Flex width="100%" alignItems="center" justifyContent="space-between">
							{leftLabel}
							{bullets}
							{rightLabel}
						</Flex>
					)
				}
			</GeometryObserver>
		</Box>
	);
}
